import { useEffect, useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "./layoutChrome";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, FileText } from "lucide-react";
import api from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import SatFiscalDocsPanel from "@/components/SatFiscalDocsPanel";
import { resolveDatosMaestrosAwareBreadcrumb } from "@/modules/datosMaestros/datosMaestrosNavigation";
import { useDatosMaestrosNavigationContext } from "@/modules/datosMaestros/useDatosMaestrosNavigationContext";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";

/**
 * Constancia CSF y opinión de cumplimiento — pantalla dedicada (no descarga masiva de CFDI).
 */
export default function DocumentosFiscalesSat() {
  const location = useLocation();
  useDatosMaestrosNavigationContext();
  const showTechnicalUi = usePlatformDesigner();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, []);

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Constancia CSF y opinión de cumplimiento"
        subtitle="Descarga automatizada de constancia de situación fiscal y opinión de cumplimiento con la e.firma del cliente."
        breadcrumb={resolveDatosMaestrosAwareBreadcrumb(
          location,
          VISOR_MODULE_BREADCRUMB_ROOT,
          "Documentos fiscales SAT",
        )}
      />

      <div className="p-8 space-y-5 max-w-7xl mx-auto">
        {showTechnicalUi ? (
        <div
          className="rounded-sm border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 leading-relaxed flex gap-3"
          role="note"
          data-testid="documentos-fiscales-owner-note"
        >
          <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" strokeWidth={1.75} aria-hidden />
          <p className="m-0">
            <strong>Documentos fiscales SAT</strong> — pantalla canónica (dueño técnico) de CSF y opinión. Estos
            documentos <strong>no son CFDI/XML</strong>. Requiere e.firma en{" "}
            <Link to="/clientes" className="text-[#0F2D5C] underline font-medium">
              Clientes
            </Link>
            . Para descarga masiva de comprobantes use{" "}
            <Link to="/descarga-automatica-sat" className="text-[#0F2D5C] underline font-medium">
              Descarga de CFDI SAT
            </Link>
            .
          </p>
        </div>
        ) : null}

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4 shadow-sm">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger
                data-testid="documentos-fiscales-client-select"
                className="mt-1 h-auto min-h-[40px] py-2 text-left"
              >
                <SelectValue placeholder="Selecciona un cliente" />
              </SelectTrigger>
              <SelectContent className="max-w-[600px]">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="py-2">
                    <div className="flex flex-col items-start text-left">
                      <div className="font-bold text-sm whitespace-normal leading-tight">{c.razon_social}</div>
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">
                        {c.rfc} {c.owner_email ? ` · ${c.owner_email}` : ""}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 text-[#0F2D5C] border-t border-slate-100 pt-4">
            <FileText className="w-4 h-4 shrink-0" strokeWidth={1.8} aria-hidden />
            <p className="text-sm font-semibold m-0">Documentos ante el SAT</p>
          </div>

          <SatFiscalDocsPanel clientId={clientId} />
        </div>
      </div>
    </div>
  );
}
